<template>
	<div :class="['app-shell', { 'is-embedded': embedded }]">
		<div class="shell-content" ref="shell">
			<Navbar
				v-if="!embedded"
				:date-range="dateRange"
				@update:search="onSearch"
				@update:date-range="onFetchDateRange"
				@update:violation="applyFiltersOnly"
				@toggle-unallocated="applyFiltersOnly"
				@clear-filters="clearFilters"
			/>
			<div class="app-main">
				<section class="center-panel">
					<PlanningDutyTable
						:drivers="drivers"
						:embedded="embedded"
						:target-date="targetDate"
						:selectable="selectable"
						:selected-driver-id="selectedDriverId"
						:driver-violations-counts="driverViolationsCounts"
						:custom-duties="customDuties"
						@select-driver="emit('select-driver', $event)"
					/>
				</section>

				<aside v-if="!embedded && planningStore.filters.showRightPanel" class="right-panel" :class="{ 'de-ch-width': isCurrentLocalGerman }">
					<IndicatorFilterComponent
						class="panel-content"
						:groups="GROUPS"
						@apply="onIndicatorSelection"
						@close="planningStore.setShowRightPanel(false)"
					/>
				</aside>
			</div>
		</div>

		<aside v-if="!embedded && showRightPanel" class="preview-panel-wrapper">
			<DutyChangePreviewPanel :preview="previewData" @close="closePreview" @publish="handlePublishPreview" />
		</aside>
	</div>
</template>
<script setup lang="ts">
import { ref, onBeforeMount, computed, onMounted, onUnmounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';

import Navbar from '../components/Layout/Navbar.vue';
import IndicatorFilterComponent from '../components/Layout/IndicatorFilterComponent.vue';
import PlanningDutyTable from '../components/Layout/PlanningDutyTable.vue';
import DutyChangePreviewPanel from '@/rennovation/planning-view/components/Layout/Modals/Preview/DutyChangePreviewPanel.vue';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';
import { useUserGroupBridge } from '../store/useUserGroupBridge';
import { usePlanningStore } from '@/rennovation/planning-view/store/usePlanningStore';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';
import { usePlanningBridgeStore } from '../store/bridge/planningBridge';
import { useDutyStore, useUserStore } from '../store';
import { useDaysBridgeStore } from '@/rennovation/planning-view/store/bridge/daysBridge';

import type { IndicatorGroup } from '../types/components/types';
import type { SelectedIndicator } from '../composables/useDutyStyles';
import type { IndicatorOption } from '../types/state/planning-view-state';
import type { DateRange } from '../../shared/types/date-range-picker';
import { STORAGE_KEYS, STORAGE_VIEW_KEY, STORAGE_DISPOSITION_KEY, SCROLL_STORAGE_KEY } from '../constants';
import { ApiDriversResponse } from '@/rennovation/planning-view/types/api/users-response';
import { ApiDuty } from '@/rennovation/planning-view/types/api/duties-response';
import metricsService from '@/services/metricsService.js';

// Store Invocations
const dataStore = usePlanningBridgeStore();
const dayStore = useDayStore();
const planningStore = usePlanningStore();
const dutyStore = useDutyStore();
const daysBridge = useDaysBridgeStore();
const userGroupBridge = useUserGroupBridge();
const userStore = useUserStore();
const { t } = useI18n();

const props = withDefaults(
	defineProps<{
		embedded?: boolean;
		targetDriverId?: string | null;
		targetDriverIds?: string[] | null;
		targetDate?: string | null;
		selectable?: boolean;
		selectedDriverId?: string | null;
		driverViolationsCounts?: Record<string, number> | null;
		rangeFromStatus?: string | null;
		rangeToStatus?: string | null;
		customDrivers?: ApiDriversResponse[] | null;
		customDuties?: Record<string, Record<string, ApiDuty>> | null;
	}>(),
	{
		embedded: false,
		targetDriverId: null,
		targetDriverIds: null,
		targetDate: null,
		selectable: false,
		selectedDriverId: null,
		driverViolationsCounts: null,
		rangeFromStatus: null,
		rangeToStatus: null,
		customDrivers: null,
		customDuties: null,
	}
);

const emit = defineEmits(['select-driver']);
const shell = ref<HTMLDivElement | null>(null);

// Static Defaults
const DEFAULT_INDICATOR: IndicatorOption[] = [
	{ itemKey: 'approvedViolation', groupKey: 'violations' },
	{ itemKey: 'unapprovedLawViolation', groupKey: 'violations' },
	{ itemKey: 'unapprovedViolation', groupKey: 'violations' },
	{ itemKey: 'hasMet', groupKey: 'preferences' },
	{ itemKey: 'hasUnmet', groupKey: 'preferences' },
	{ itemKey: 'partiallyMet', groupKey: 'preferences' },
	{ itemKey: 'pendingHigh', groupKey: 'absences' },
	{ itemKey: 'approvedHigh', groupKey: 'absences' },
	{ itemKey: 'pendingLow', groupKey: 'absences' },
	{ itemKey: 'approvedLow', groupKey: 'absences' },
	{ itemKey: 'preferred_workday_pending', groupKey: 'preferred_workday' },
	{ itemKey: 'preferred_workday_approved', groupKey: 'preferred_workday' },
	{ itemKey: 'internalNotes', groupKey: 'dutyFlags' },
	{ itemKey: 'fixedDuty', groupKey: 'dutyFlags' },
	{ itemKey: 'workReport', groupKey: 'dutyFlags' },
	{ itemKey: 'modifiedDuty', groupKey: 'dutyFlags' },
	{ itemKey: 'exchangeDuty', groupKey: 'dutyFlags' },
];

let originalFiltersBackup: string | null = null;

const { currentPreview, showRightPanel, loadPreview, cancelPreview, publishPreview } = useDutyChangePreview();

const previewData = computed(() => currentPreview.value);

const closePreview = async () => {
	await cancelPreview();
};

const handlePublishPreview = async () => {
	await publishPreview();
};

const resolvedTargetDriverIds = computed<string[]>(() => {
	if (props.targetDriverIds && props.targetDriverIds.length > 0) return props.targetDriverIds;
	if (props.targetDriverId) return [props.targetDriverId];
	return [];
});

const backupGlobalFilters = () => {
	originalFiltersBackup = JSON.stringify(planningStore.filters);
};

const restoreGlobalFilters = () => {
	if (originalFiltersBackup && props.embedded) {
		planningStore.filters = JSON.parse(originalFiltersBackup);
	}
};

// Helper to access LocalStorage with fallback
const getStorageData = <T = Record<string, any>,>(key: string, fallback: T): T => {
	try {
		const raw = localStorage.getItem(key);
		return raw ? JSON.parse(raw) : fallback;
	} catch {
		return fallback;
	}
};

const updatePlanningStorage = (patch: Record<string, any>) => {
	const current = getStorageData(STORAGE_KEYS.PLANNING_TABLE_DATA, {});
	localStorage.setItem(STORAGE_KEYS.PLANNING_TABLE_DATA, JSON.stringify({ ...current, ...patch }));
};

// Initial Hydration
const planningTableData = getStorageData(STORAGE_KEYS.PLANNING_TABLE_DATA, {} as Record<string, any>);

const getDefaultDateRange = () => ({
	from: DateTime.now().minus({ days: 10 }).toISODate(),
	to: DateTime.now().minus({ days: 10 }).plus({ days: 79 }).toISODate(),
});

const setDateRange = () => {
	const { from, to } = getDefaultDateRange();

	planningStore.filters.fromDate = from;
	planningStore.filters.toDate = to;
	planningStore.filters.firstDate = from;

	updatePlanningStorage({ fromDate: from, toDate: to, firstDate: from });
};

const calculateTimelineRange = async () => {
	if (props.embedded && props.rangeFromStatus && props.rangeToStatus) {
		if (!dayStore.dateRangesByStatusLoaded) {
			await dayStore.loadDateRangeForDayStatuses();
		}

		const fromRange = dayStore.getDateRangeByStatusName(props.rangeFromStatus);
		const toRange = dayStore.getDateRangeByStatusName(props.rangeToStatus);

		const resolvedFromIso = fromRange?.from ?? toRange?.from ?? null;
		const resolvedToIso = toRange?.to ?? fromRange?.to ?? null;

		if (resolvedFromIso && resolvedToIso) {
			const from = DateTime.fromISO(resolvedFromIso);
			const to = DateTime.fromISO(resolvedToIso);

			planningStore.filters.fromDate = from.toISODate();
			planningStore.filters.toDate = to.toISODate();
			planningStore.filters.firstDate = from.toISODate();
			firstDate.value = from.toISODate();
			return;
		}
	}
};

// Hydrate filter states
if (planningTableData.violations) planningStore.filters.violations = planningTableData.violations;
if (planningTableData.userList) planningStore.filters.userList = planningTableData.userList;
if (planningTableData.selectedUserGroups) planningStore.filters.selectedUserGroups = planningTableData.selectedUserGroups;
if (planningTableData.selectedDay) planningStore.filters.selectedDay = planningTableData.selectedDay;
if (planningTableData.searchText) planningStore.filters.searchQuery = planningTableData.searchText;
if (planningTableData.userStatus) planningStore.filters.userStatus = planningTableData.userStatus;

if (planningTableData.fromDate && planningTableData.toDate) {
	planningStore.filters.fromDate = planningTableData.fromDate;
	planningStore.filters.toDate = planningTableData.toDate;
	planningStore.filters.firstDate = planningTableData.firstDate;
} else {
	setDateRange();
}

const firstDate = ref(planningStore.filters.firstDate);

const isCurrentLocalGerman = computed(() => localStorage.getItem('locale') === 'de-CH');

const dateRange = computed<DateRange>(() => ({
	startDate: planningStore.filters.fromDate ? DateTime.fromISO(planningStore.filters.fromDate).toJSDate() : null,
	endDate: planningStore.filters.toDate ? DateTime.fromISO(planningStore.filters.toDate).toJSDate() : null,
}));

const GROUPS = computed<IndicatorGroup[]>(() => [
	{
		key: 'violations',
		label: t('duty_violations'),
		items: [
			{ key: 'approvedViolation', label: t('violation.approved'), indicatorClass: 'dot-approved' },
			{ key: 'unapprovedLawViolation', label: t('violation.unapproved_law'), indicatorClass: 'dot-unapproved-law' },
			{ key: 'unapprovedViolation', label: t('violation.unapproved'), indicatorClass: 'dot-unapproved' },
		],
	},
	{
		key: 'preferences',
		label: t('preferences'),
		items: [
			{ key: 'hasMet', label: t('core_dashboard_metCount'), indicatorClass: 'dot-met' },
			{ key: 'hasUnmet', label: t('core_dashboard_unmetCount'), indicatorClass: 'dot-not-met' },
			{ key: 'partiallyMet', label: t('core_dashboard_partially_met'), indicatorClass: 'dot-partial' },
		],
	},
	{
		key: 'absences',
		label: t('user_edit_absences'),
		items: [
			{ key: 'pendingHigh', label: t('indicator.pending_absence_high'), indicatorClass: 'dot-pending-high' },
			{ key: 'approvedHigh', label: t('indicator.approved_absence_high'), indicatorClass: 'dot-approved-high' },
			{ key: 'pendingLow', label: t('indicator.pending_absence_low'), indicatorClass: 'dot-pending-low' },
			{ key: 'approvedLow', label: t('indicator.approved_absence_low'), indicatorClass: 'dot-approved-low' },
		],
	},
	{
		key: 'preferred_workday',
		label: t('preferred_workday'),
		items: [
			{ key: 'preferred_workday_pending', label: t('absence.pending'), indicatorClass: 'dot-preferred_workday_pending' },
			{ key: 'preferred_workday_approved', label: t('absence.approved'), indicatorClass: 'dot-preferred_workday_approved' },
		],
	},
	{
		key: 'dutyFlags',
		label: t('duty_flags'),
		items: [
			{ key: 'internalNotes', label: t('internal_notes'), icon: 'mdi-rectangle' },
			{ key: 'fixedDuty', label: t('fixed_duty_locked'), icon: 'mdi-lock' },
			{ key: 'workReport', label: t('work_report'), icon: 'mdi-file-document' },
			{ key: 'modifiedDuty', label: t('modified_duty'), icon: 'mdi-circle' },
			{ key: 'exchangeDuty', label: t('exchange_duty'), icon: 'mdi-swap-vertical' },
		],
	},
]);

// Helper: Extract user IDs from selectedUserGroups state
const selectedGroupUserIds = computed(() => {
	const selected = planningStore.filters.selectedUserGroups?.selected ?? [];

	return selected.flatMap((item: any) => (item.children?.length ? item.children.map((child: any) => child.value) : item.value ? [item.value] : []));
});

const drivers = computed(() => {
	if (props.customDrivers !== null) {
		return props.customDrivers;
	}
	if (props.embedded) {
		if (resolvedTargetDriverIds.value.length > 0) {
			return planningStore.allUsers.filter((driver) => resolvedTargetDriverIds.value.includes(driver.id));
		}
		const fallback = planningStore.allUsers[0];
		return fallback ? [fallback] : [];
	}

	if (planningStore.filters.userStatus === 'all') return planningStore.allUsers;
	return planningStore.filters.userStatus === 'active' ? planningStore.activeUsers : planningStore.inactiveUsers;
});

// Centralized combined user filter evaluator
const evaluateCombinedUserFilters = async () => {
	const showUnallocated = getStorageData<boolean>(STORAGE_KEYS.SHOW_UNALLOCATED_DRIVERS, false);
	const groupIds = selectedGroupUserIds.value;

	if (showUnallocated && groupIds.length) {
		const unallocatedUsers = await userStore.loadUnallocatedUsers(Object.keys(dayStore.daysRef));
		const groupSet = new Set(groupIds);
		planningStore.filters.userList = unallocatedUsers.filter((id) => groupSet.has(id));
		return;
	}

	if (showUnallocated) {
		planningStore.filters.userList = await userStore.loadUnallocatedUsers(Object.keys(dayStore.daysRef));
		return;
	}

	if (groupIds.length) {
		planningStore.filters.userList = groupIds;
		return;
	}

	planningStore.filters.userList = null;
};

const triggerBackgroundDutyFetch = () => {
	const { fromDate: from, toDate: to, selectedDay, firstDate: fDate } = planningStore.filters;
	const statsDate = selectedDay?.dateString || fDate;
	const targetUsers = planningStore.allUsers.map((d: any) => d.id || d._id).filter(Boolean);

	if (from && to && targetUsers.length > 0) {
		dutyStore.startBackgroundDutiesFetch({ from, to, statsDate, users: targetUsers, chunkSize: 20 });
	}
};

const initPlanning = async () => {
	await calculateTimelineRange();

	if (props.embedded && !props.customDrivers && resolvedTargetDriverIds.value.length > 0) {
		planningStore.filters.filteredDrivers = resolvedTargetDriverIds.value;
	}
	try {
		await Promise.all([
			dayStore.initDays({ firstDate: firstDate.value }),
			planningStore.load({ firstDate: firstDate.value }),
			Object.keys(dataStore.setup).length === 0 ? dataStore.loadSetup() : Promise.resolve(),
		]);

		const dayIds = Object.values(dayStore.days)
			.map((d: any) => d?.data?.id || d?.data?._id || d?._id || d?.id)
			.filter(Boolean);
		if (dayIds.length > 0) {
			await dutyStore.getUnallocatedDuties({ days: dayIds });
		}

		triggerBackgroundDutyFetch();
	} catch (error) {
		console.error('Failed to initialize scheduler:', error);
	}
};

const onFetchDateRange = async (updatedRange: { startDate?: Date | null; endDate?: Date | null }) => {
	localStorage.removeItem(SCROLL_STORAGE_KEY);
	if (props.embedded) return;
	planningStore.setInitial(true);

	await nextTick();

	const isCleared = !updatedRange.startDate && !updatedRange.endDate;

	let from = planningStore.filters.fromDate;
	let to = planningStore.filters.toDate;

	if (isCleared) {
		({ from, to } = getDefaultDateRange());
	} else {
		if (updatedRange.startDate) {
			from = DateTime.fromJSDate(updatedRange.startDate).toISODate();
		}

		if (updatedRange.endDate) {
			to = DateTime.fromJSDate(updatedRange.endDate).toISODate();
		}
	}

	planningStore.filters.fromDate = from;
	planningStore.filters.toDate = to;
	planningStore.filters.firstDate = from;
	firstDate.value = from;

	updatePlanningStorage({
		fromDate: from,
		toDate: to,
		firstDate: from,
	});

	await initPlanning();
	await fetchData();
	planningStore.setInitial(false);
};

const onIndicatorSelection = async (selection: SelectedIndicator[]) => {
	if (props.embedded) {
		return;
	}
	localStorage.setItem(STORAGE_KEYS.INDICATOR_FILTERS, JSON.stringify(selection));
	planningStore.setIndicatorFilter(selection);
};

const applyUserFilters = async () => {
	const showUnallocated = getStorageData<boolean>(STORAGE_KEYS.SHOW_UNALLOCATED_DRIVERS, false);
	const hasGroups = planningStore.filters.selectedUserGroups?.selected?.length > 0;

	await evaluateCombinedUserFilters();

	const hasUserFilter = showUnallocated || hasGroups;
	planningStore.filters.filteredDrivers = hasUserFilter ? planningStore.filters.userList : planningStore.allDriverIds;
};

const fetchData = async () => {
	if (props.embedded) return;
	metricsService.startTracking('PlanningView-FetchData');
	planningStore.setInitial(true);

	try {
		await applyUserFilters();

		if (planningStore.filters.violations && planningStore.filters.filteredDrivers?.length > 0) {
			await planningStore.fetchFilterResult();
		}

		updatePlanningStorage({
			fromDate: planningStore.filters.fromDate,
			toDate: planningStore.filters.toDate,
			firstDate: planningStore.filters.firstDate,
			violations: planningStore.filters.violations,
			userList: planningStore.filters.userList,
			selectedUserGroups: planningStore.filters.selectedUserGroups,
			filteredDrivers: planningStore.filters.filteredDrivers,
		});
	} finally {
		planningStore.setInitial(false);
		nextTick(() => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					metricsService.endTracking('PlanningView-FetchData', 'PlanningShell.vue');
				});
			});
		});
	}
};

const applyFiltersOnly = async () => {
	if (props.embedded) return;
	await applyUserFilters();
	if (planningStore.filters.violations && planningStore.filters.filteredDrivers?.length > 0) {
		await planningStore.fetchFilterResult();
	}
	updatePlanningStorage({
		violations: planningStore.filters.violations,
		userList: planningStore.filters.userList,
		selectedUserGroups: planningStore.filters.selectedUserGroups,
		filteredDrivers: planningStore.filters.filteredDrivers,
	});
};

const onSearch = (value: string) => {
	if (props.embedded) return;
	planningStore.filters.searchQuery = value;
	updatePlanningStorage({
		searchText: value,
	});
	fetchData();
};

const clearFilters = async () => {
	planningStore.setInitial(true);
	setDateRange();
	await nextTick();

	localStorage.removeItem(STORAGE_KEYS.PLANNING_TABLE_DATA);
	localStorage.removeItem(STORAGE_VIEW_KEY);
	localStorage.removeItem(STORAGE_DISPOSITION_KEY);
	localStorage.removeItem(STORAGE_KEYS.SHOW_UNALLOCATED_DRIVERS);

	Object.assign(planningStore.filters, {
		violations: null,
		userList: [],
		selectedUserGroups: {
			selected: [],
		},
		selectedDay: null,
		searchQuery: '',
		userStatus: 'all',
	});

	firstDate.value = planningStore.filters.firstDate;

	updatePlanningStorage({
		fromDate: planningStore.filters.fromDate,
		toDate: planningStore.filters.toDate,
		firstDate: planningStore.filters.firstDate,
	});

	await initPlanning();
	planningStore.setInitial(false);
};

const adjustPreviewHeight = () => {
	if (!showRightPanel.value) return;

	setTimeout(() => {
		const previewContainer: HTMLDivElement | null = document.querySelector('.preview-panel-wrapper');

		if (previewContainer) {
			previewContainer.style.height = `${shell.value.clientHeight}px`;
		}
	}, 150);
};

onBeforeMount(async () => {
	metricsService.startTracking('PlanningView-Init');
	dutyStore.clearCache();
	loadPreview();
	if (props.embedded) {
		backupGlobalFilters();
		planningStore.setShowRightPanel(false);
		planningStore.filters.searchQuery = '';
		planningStore.filters.selectedUserGroups = [];
		planningStore.filters.userList = [];
		planningStore.filters.violations = null;
		planningStore.filters.userStatus = 'all';
		planningStore.filters.filteredDrivers = null;
		planningStore.filters.combinedFilteredDrivers = null;
		planningStore.filters.userList = null;
		localStorage.removeItem(STORAGE_VIEW_KEY);
		localStorage.removeItem(STORAGE_DISPOSITION_KEY);
	}
	planningStore.setInitial(true);

	try {
		const savedSelections = getStorageData<SelectedIndicator[] | null>(STORAGE_KEYS.INDICATOR_FILTERS, null);
		const filtersToApply = Array.isArray(savedSelections) && savedSelections.length > 0 ? savedSelections : DEFAULT_INDICATOR;

		planningStore.setIndicatorFilter(filtersToApply);

		await Promise.all([userGroupBridge.fetchUserGroups(), userGroupBridge.fetchUsers()]);

		await initPlanning();
		await fetchData();

		await daysBridge.loadFirstPastDay();
	} catch (error) {
		console.error('[Shell Initialization Error]:', error);
	} finally {
		planningStore.setInitial(false);
		nextTick(() => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					metricsService.endTracking('PlanningView-Init', 'PlanningShell.vue');
				});
			});
		});
	}
});

onMounted(() => {
	adjustPreviewHeight();
});

onUnmounted(() => {
	dutyStore.terminateWorker();
});

onBeforeUnmount(() => {
	restoreGlobalFilters();
});

watch(
	() => [resolvedTargetDriverIds.value.join(','), props.targetDate],
	async () => {
		if (props.embedded) {
			planningStore.setInitial(true);
			await nextTick();
			await initPlanning();
			planningStore.setInitial(false);
		}
	}
);
watch(
	() => planningStore.filters.selectedUserGroups,
	async () => {
		await applyFiltersOnly();
	},
	{ deep: true }
);
watch(
	() => showRightPanel.value,
	(v: boolean) => {
		adjustPreviewHeight();
	}
);
</script>

<style scoped>
.app-shell {
	display: flex;
	overflow: hidden;
	height: 100%;
	align-items: start;
}

.app-shell.is-embedded {
	height: 100% !important;
	margin: 0 !important;
}

.shell-content {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.preview-panel-wrapper {
	width: 350px;
	flex-shrink: 0;
	overflow: hidden;
	padding-top: 54px;
}

.app-main {
	flex: 1;
	display: flex;
	min-height: 0;
	overflow-y: auto;
}

.center-panel {
	flex: 1;
	min-width: 0;
	overflow: hidden;
	display: flex;
	flex-direction: column;
}

.right-panel {
	width: 260px;
	overflow: hidden;
}

.right-panel.de-ch-width {
	width: 320px;
	min-width: 320px;
}

.right-panel .panel-content {
	height: calc(100vh - 150px);
	overflow-y: auto;
	padding: 12px;
}
</style>
